import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { ToursHeader } from 'pages/tours/search-result-page/components/tours-header'
// import { TourDetailGuide } from 'pages/tours/tour-detail-page/components/tour-detail-guide'
import { useAboutTour } from 'pages/tours/tour-detail-page/model/hooks/useAboutTour'

import { selectionStore } from 'entities/tour/models'
import { setSettingForApp } from 'entities/user/module/app-settings'

import { Hotel, Tour } from 'shared/api/tours/search'
import { ShareKeys } from 'shared/constants'
import EmptyImage from 'shared/images/airplane.svg'
import { checkUserAuth } from 'shared/lib/checkUserAuth'
import { getRedirectUrl } from 'shared/lib/getRedirectUrl'
import { AppAdviceText } from 'shared/ui/app-advice-text'
import { ImageCarousel } from 'shared/ui/image-carousel'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'
import { ShareButton } from 'shared/ui/share-button'

import { getShareUrl } from '../appeal-details/libs/getShareUrl'

import { HotelInfo, ListInfo, MiniMap, RoomsList } from './components'
import { RoomsListSkeleton } from './components/rooms-list/components/room-list-skeleton'
import s from './styles.module.scss'

interface TourDetailProps {
  title?: string
  subtitle?: string
  isError?: boolean
  isLoading: boolean
  isRoomLoading?: boolean
  isViewMode?: boolean
  hotelDetail: Hotel | null
  isSharingMode?: boolean
  hotelSearchSessionId?: string
}

export const TourDetail = observer(
  ({
    title,
    isError,
    isRoomLoading,
    subtitle,
    isLoading,
    hotelDetail,
    isViewMode = false,
    isSharingMode = false,
    hotelSearchSessionId,
  }: TourDetailProps) => {
    const { t } = useTranslation()

    const navigate = useNavigate()

    const aboutTour = useAboutTour()

    const goToGallery = () => {
      navigate(RoutingPaths.tours.gallery.path(), {
        state: {
          hotelName: hotelDetail?.hotel_name,
          images: hotelDetail?.hotel_images,
        },
      })
    }

    const handleGoBack = () => {
      const hasPreviousPages =
        window.history.length > 1 &&
        (document.referrer.includes(window.location.origin) ||
          window.history.length > 2)

      if (hasPreviousPages) {
        navigate(-1)
      } else {
        navigate(RoutingPaths.tours.searchResults.path())
      }
    }

    const galleryImages = useMemo(() => {
      return hotelDetail?.hotel_images?.map((i) => ({
        id: i.sort_index,
        image: i.src,
        image_fullpath: i.src,
      }))
    }, [hotelDetail?.hotel_images])

    const handleSelectRoom = async (room: Tour) => {
      const navigatePath = RoutingPaths.tours.hotelRoomDetails.path()
      const redirectUrl = getRedirectUrl(navigatePath)

      await checkUserAuth({
        redirectUrl,
        onSuccess: () => {
          if (room) {
            selectionStore.setSelectedRoom(room)
            void setSettingForApp({ key: ShareKeys.hotel, value: '' })

            navigate(navigatePath)
          }
        },
        onError: () => {
          const payload = {
            hotel: hotelDetail,
            room: room,
            hotelSearchSessionId: hotelSearchSessionId || '',
          }
          void setSettingForApp({
            key: ShareKeys.hotel,
            value: JSON.stringify(payload),
          })
        },
      })
    }

    if (isError) {
      return (
        <div className={clsx('container', s.errorWrapper)}>
          <div className={s.error}>
            <PopupContent
              image={EmptyImage}
              title={t('error')}
              button={t('back')}
              description={t('tours_block.error_hotel_detal')}
              onClick={() => navigate(-1)}
            />
          </div>
        </div>
      )
    }

    if (isLoading || !aboutTour || !hotelDetail) {
      return (
        <div className="container">
          <Loader marginTop={200} />
          <AppAdviceText />
        </div>
      )
    }

    const shareUrl = getShareUrl(
      RoutingPaths.tours.tourDetail.pathShare(hotelDetail?.sharing_code || ''),
    )

    const longitude = Number(hotelDetail?.longitude)
    const latitude = Number(hotelDetail?.latitude)

    return (
      <>
        <ToursHeader
          onGoBack={handleGoBack}
          title={title}
          subtitle={subtitle}
          actionRight={
            !isViewMode && (
              <ShareButton
                shareUrl={shareUrl}
                text={t('tours_block.share_title', {
                  hotelName: hotelDetail?.hotel_name || '',
                  stars: hotelDetail?.hotel_stars || '',
                })}
              />
            )
          }
        />
        <ImageCarousel
          images={galleryImages || []}
          onClick={goToGallery}
        />
        <div className={s.container}>
          <HotelInfo data={hotelDetail} />

          {isRoomLoading ? (
            <RoomsListSkeleton />
          ) : (
            <RoomsList
              rooms={hotelDetail?.tours}
              isSharingMode={isSharingMode}
              onRoomClick={handleSelectRoom}
              isViewMode={isViewMode}
            />
          )}

          {!!longitude && !!latitude && (
            <MiniMap
              hotelName={hotelDetail?.hotel_name}
              longitude={longitude}
              latitude={latitude}
              title={t('tours_block.on_the_map')}
            />
          )}

          <ListInfo
            aboutTourData={aboutTour}
            aboutHotelData={hotelDetail?.hotel_descr || ''}
            hotelId={hotelDetail?.top_hotels_info?.id}
          />

          {/* <TourDetailGuide /> */}
        </div>
      </>
    )
  },
)
